import { Link } from 'react-router-dom';
import { StocksStatusLightColor } from '@/models';
import type { StocksStatusLight } from '@/models';
import { formatDate } from '@/utils/helpers';
import { MARKET_ROUTES } from '@/constants/routes';

interface TrafficLightCardProps {
  stockStatusLight: StocksStatusLight | null;
  className?: string;
}

type TrafficLightStatus = 'red' | 'yellow' | 'green';

const colorToStatus = (color: StocksStatusLightColor): TrafficLightStatus => {
  switch (color) {
    case StocksStatusLightColor.RED:
      return 'red';
    case StocksStatusLightColor.YELLOW:
      return 'yellow';
    case StocksStatusLightColor.GREEN:
      return 'green';
  }
};

const getStatusLabel = (status: TrafficLightStatus): string => {
  switch (status) {
    case 'red':
      return 'Vorsicht';
    case 'yellow':
      return 'Neutral';
    case 'green':
      return 'Alles im grünen Bereich';
  }
};

export default function TrafficLightCard({ stockStatusLight, className = '' }: TrafficLightCardProps) {
  const status = stockStatusLight !== null ? colorToStatus(stockStatusLight.color) : 'yellow';
  const statusLabel = getStatusLabel(status);
  const statusDescription = stockStatusLight?.comment || '';

  return (
    <Link
      to={MARKET_ROUTES.ANALYSIS}
      className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 h-[320px] flex flex-col hover:shadow-lg transition-shadow cursor-pointer ${className}`}
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
        Börsen-Ampel
      </h3>

      {/* Traffic Light */}
      <div className="flex flex-col items-center flex-1 justify-center">
            <div className="flex flex-col gap-3 mb-4">
              {/* Red Light */}
              <div
                className={`w-12 h-12 rounded-full border-4 transition-all ${
                  status === 'red'
                    ? 'bg-red-500 border-red-600 shadow-lg shadow-red-500/50 dark:bg-red-400 dark:border-red-500'
                    : 'bg-red-200 border-red-300 opacity-30 dark:bg-red-900/30 dark:border-red-800'
                }`}
              />

              {/* Yellow Light */}
              <div
                className={`w-12 h-12 rounded-full border-4 transition-all ${
                  status === 'yellow'
                    ? 'bg-yellow-400 border-yellow-500 shadow-lg shadow-yellow-400/50 dark:bg-yellow-300 dark:border-yellow-400'
                    : 'bg-yellow-100 border-yellow-200 opacity-30 dark:bg-yellow-900/30 dark:border-yellow-800'
                }`}
              />

              {/* Green Light */}
              <div
                className={`w-12 h-12 rounded-full border-4 transition-all ${
                  status === 'green'
                    ? 'bg-green-500 border-green-600 shadow-lg shadow-green-500/50 dark:bg-green-400 dark:border-green-500'
                    : 'bg-green-200 border-green-300 opacity-30 dark:bg-green-900/30 dark:border-green-800'
                }`}
              />
            </div>

            {/* Status Label */}
            <div className="text-center max-w-full px-2">
              <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                {statusLabel}
              </p>
              {statusDescription && (
                <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                  {statusDescription}
                </p>
              )}
              {stockStatusLight !== null && stockStatusLight.updated !== null && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  Stand: {formatDate(stockStatusLight.updated)}
                </p>
              )}
            </div>
          </div>
    </Link>
  );
}
